import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { AlertTriangle, CheckCircle, Lock, FileText, Camera } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const zar = (v) => `R ${Number(v||0).toLocaleString('en-ZA', {maximumFractionDigits:0})}`;

export default function PublicDealPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [needPin, setNeedPin] = useState(false);
  const [needAuth, setNeedAuth] = useState(false);
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchIt = async (pinVal) => {
    setLoading(true); setErr(null);
    try {
      const url = `${API_URL}/api/public/deal/${token}${pinVal?`?pin=${encodeURIComponent(pinVal)}`:''}`;
      const r = await axios.get(url);
      setData(r.data); setNeedPin(false); setNeedAuth(false);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || 'Failed to load';
      if (status === 401 && detail.toLowerCase().includes('pin')) setNeedPin(true);
      else if (status === 401 && detail.toLowerCase().includes('sign in')) setNeedAuth(true);
      else setErr(detail);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchIt(); /* eslint-disable-next-line */ }, [token]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin h-10 w-10 rounded-full border-b-2 border-emerald-600"></div></div>;

  if (needAuth) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <Card className="max-w-md w-full"><CardContent className="p-8 text-center">
        <Lock className="h-10 w-10 mx-auto text-[#1D2D2A] mb-4" />
        <h2 className="text-xl font-bold mb-2">Sign in required</h2>
        <p className="text-[#6C757D] mb-4">This deal is only visible to logged-in portal users.</p>
        <Button onClick={() => window.location.href=`/login?next=/deal/${token}`}>Go to login</Button>
      </CardContent></Card>
    </div>
  );

  if (needPin) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <Card className="max-w-md w-full"><CardContent className="p-8">
        <Lock className="h-10 w-10 mx-auto text-[#1D2D2A] mb-4" />
        <h2 className="text-xl font-bold mb-2 text-center">Enter access PIN</h2>
        <p className="text-[#6C757D] mb-4 text-center">This deal is protected by a PIN. Enter the code you were given.</p>
        <Input value={pin} onChange={e=>setPin(e.target.value)} placeholder="PIN" />
        <Button className="mt-4 w-full" onClick={()=>fetchIt(pin)}>Unlock</Button>
      </CardContent></Card>
    </div>
  );

  if (err) return <div className="min-h-screen flex items-center justify-center p-6"><p className="text-red-600">{err}</p></div>;
  if (!data) return null;

  const { proposal={}, items=[], schedules=[], totals={} } = data;
  const meets = totals.meets_minimum;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-[#1D2D2A] text-white py-8 px-6"><div className="max-w-5xl mx-auto">
        <img src="/PTC_LOGO_AM_green.svg" alt="Plane Tree Capital" className="h-10 mb-4" onError={(e)=>{e.target.style.display='none';}} />
        <h1 className="text-3xl font-bold">Loan Proposal &amp; Security Schedule</h1>
        <p className="text-slate-300 mt-1">{proposal.borrower || 'Facility'}</p>
      </div></div>

      <div className="max-w-5xl mx-auto p-6 space-y-6">
        {/* Terms */}
        <Card><CardContent className="p-6">
          <h2 className="text-xl font-bold mb-4 text-[#1D2D2A]">Facility Terms</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div><p className="text-xs text-[#6C757D]">Borrower</p><p className="font-semibold">{proposal.borrower||'—'}</p></div>
            <div><p className="text-xs text-[#6C757D]">Loan amount</p><p className="font-semibold">{zar(proposal.facility_amount)}</p></div>
            <div><p className="text-xs text-[#6C757D]">Term</p><p className="font-semibold">{proposal.term_months||0} months</p></div>
            <div><p className="text-xs text-[#6C757D]">Interest rate</p><p className="font-semibold">{proposal.interest_rate_pct||0}% p.a.</p></div>
          </div>
          {proposal.executive_summary && <div className="mt-4 text-[#6C757D] whitespace-pre-line" dangerouslySetInnerHTML={{__html: proposal.executive_summary}} />}
          {proposal.lender_bullets && proposal.lender_bullets.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-[#1D2D2A] mb-2">What the lender receives</h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                {proposal.lender_bullets.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
            </div>
          )}
        </CardContent></Card>

        {/* Cover ratios */}
        <Card className={meets ? 'bg-emerald-50' : 'bg-red-50'}><CardContent className="p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div><p className="text-xs text-[#6C757D]">Actual pool</p><p className="font-bold">{zar(totals.total_actual)}</p></div>
          <div><p className="text-xs text-[#6C757D]">Discounted pool</p><p className="font-bold">{zar(totals.total_discounted)}</p></div>
          <div><p className="text-xs text-[#6C757D]">Actual cover</p><p className="font-bold">{totals.cover_actual_pct}%</p></div>
          <div><p className="text-xs text-[#6C757D]">Discounted cover</p><p className={`font-bold ${meets?'text-emerald-700':'text-red-700'}`}>{totals.cover_discounted_pct}% {meets?<CheckCircle className="inline h-4 w-4" />:<AlertTriangle className="inline h-4 w-4" />}</p></div>
        </CardContent></Card>

        {/* Items */}
        <Card><CardContent className="p-6">
          <h2 className="text-xl font-bold mb-4 text-[#1D2D2A]">Security Schedule</h2>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Ref</TableHead><TableHead>Category</TableHead><TableHead>Asset, details &amp; documents</TableHead>
              <TableHead>Qty</TableHead><TableHead>Condition</TableHead><TableHead>Location</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead className="text-right">Value</TableHead><TableHead className="text-right">Discounted</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map(i => {
                const pics = (i.photos || []).filter(p => p && p.url);
                const docs = (i.documents || []).filter(d => d && d.url);
                return (
                <TableRow key={i.id}>
                  <TableCell className="font-mono text-xs">{i.ref_code}</TableCell>
                  <TableCell><Badge variant="outline">{i.category}</Badge></TableCell>
                  <TableCell className="max-w-lg">
                    <div className="font-medium flex items-center gap-2 flex-wrap">
                      <span>{i.name}</span>
                      {pics.length > 0 && (
                        <Badge variant="outline" className="text-[10px] gap-1 border-emerald-200 text-emerald-700" data-testid={`photo-badge-${i.id}`}>
                          <Camera className="h-3 w-3" /> {pics.length}
                        </Badge>
                      )}
                      {docs.length > 0 && (
                        <Badge variant="outline" className="text-[10px] gap-1 border-slate-300 text-slate-700" data-testid={`doc-badge-${i.id}`}>
                          <FileText className="h-3 w-3" /> {docs.length}
                        </Badge>
                      )}
                    </div>
                    {i.description && <div className="text-xs text-[#6C757D] mt-1">{i.description}</div>}
                    {i.details && Object.entries(i.details).filter(([_,v])=>v).map(([k,v]) => (
                      <div key={k} className="text-xs text-[#6C757D]">{k.replace(/_/g,' ')}: <span className="font-mono">{v}</span></div>
                    ))}
                    {pics.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2" data-testid={`public-photos-${i.id}`}>
                        {pics.map(p => (
                          <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer" title={p.name}
                             className="block w-14 h-14 rounded overflow-hidden border bg-white hover:ring-2 hover:ring-emerald-400 transition">
                            <img src={p.url} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                          </a>
                        ))}
                      </div>
                    )}
                    {docs.length > 0 && (
                      <div className="mt-2 space-y-1" data-testid={`public-docs-${i.id}`}>
                        {docs.map(d => (
                          <a key={d.id} href={d.url} target="_blank" rel="noopener noreferrer"
                             className="inline-flex items-center gap-1.5 text-xs text-emerald-700 hover:underline mr-3">
                            <FileText className="h-3 w-3" /> {d.name}
                          </a>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">{i.quantity || '—'}</TableCell>
                  <TableCell className="text-xs">{i.condition || '—'}</TableCell>
                  <TableCell className="text-xs">{i.location || '—'}</TableCell>
                  <TableCell className="text-xs">{i.owner || '—'}</TableCell>
                  <TableCell className="text-right">{zar(i.value_actual)}</TableCell>
                  <TableCell className="text-right font-semibold">{zar(i.value_discounted)}</TableCell>
                </TableRow>
              );})}
              <TableRow className="bg-slate-50">
                <TableCell colSpan={7} className="font-bold">Total available securities</TableCell>
                <TableCell className="text-right font-bold">{zar(totals.total_actual)}</TableCell>
                <TableCell className="text-right font-bold">{zar(totals.total_discounted)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent></Card>

        {/* Schedules */}
        {schedules.map(s => (
          <Card key={s.id}><CardContent className="p-6">
            <h2 className="text-xl font-bold mb-1 text-[#1D2D2A]">{s.name}</h2>
            {s.subtitle && <p className="text-[#6C757D] mb-4">{s.subtitle}</p>}
            <Table>
              <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Description</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
              <TableBody>
                {(s.rows||[]).map((r,idx) => (<TableRow key={idx}><TableCell className="font-mono text-xs">{r.when}</TableCell><TableCell>{r.description}</TableCell><TableCell className="text-right">{zar(r.amount)}</TableCell></TableRow>))}
                <TableRow className="bg-slate-50"><TableCell colSpan={2} className="font-bold">Total</TableCell><TableCell className="text-right font-bold">{zar((s.rows||[]).reduce((a,r)=>a+(r.amount||0),0))}</TableCell></TableRow>
              </TableBody>
            </Table>
          </CardContent></Card>
        ))}

        {/* Use of Loan Funds */}
        {(proposal.use_of_funds || []).length > 0 && (
          <Card data-testid="public-use-of-funds"><CardContent className="p-6">
            <h2 className="text-xl font-bold mb-4 text-[#1D2D2A]">Use of Loan Funds</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {(proposal.use_of_funds || []).map((section, i) => (
                <div key={i}>
                  <h3 className="text-sm font-semibold text-[#1D2D2A] mb-2">{section.heading}</h3>
                  <ul className="list-disc list-inside space-y-1 text-sm text-slate-700">
                    {(section.items || []).map((it, j) => <li key={j}>{it}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </CardContent></Card>
        )}

        {/* The Opportunity */}
        {proposal.opportunity_narrative && (
          <Card data-testid="public-opportunity"><CardContent className="p-6">
            <h2 className="text-xl font-bold mb-4 text-[#1D2D2A]">The Opportunity</h2>
            <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-line">{proposal.opportunity_narrative}</div>
          </CardContent></Card>
        )}

        {/* Notes & Sources */}
        {proposal.notes && (
          <Card data-testid="public-notes"><CardContent className="p-6">
            <h2 className="text-xl font-bold mb-4 text-[#1D2D2A]">Notes &amp; Sources</h2>
            <div className="text-sm text-slate-700 whitespace-pre-line">{proposal.notes}</div>
          </CardContent></Card>
        )}
      </div>
    </div>
  );
}
